import { useParams, useNavigate } from "react-router-dom";
import {
  TERMS_PAGE_SIZES,
  useTerm,
  useTerms,
  wordFirstLetter,
} from "@/hooks/useTerms";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  BookOpen,
  Volume2,
  Share2,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { ThemedAudioPlayer } from "@/components/ThemedAudioPlayer";

const TermDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { data: term, isLoading } = useTerm(id || "");

  // Get related terms (same first letter)
  const { data: relatedTerms } = useTerms({
    letter: term?.word ? wordFirstLetter(term.word) : undefined,
    page: 1,
    page_size: TERMS_PAGE_SIZES.related,
    limit: TERMS_PAGE_SIZES.related,
  });

  const filteredRelatedTerms = relatedTerms
    ?.filter(row => row.id !== id)
    ?.slice(0, 3);

  const handleShare = async () => {
    try {
      await navigator.share({
        title: term?.word,
        text: term?.word ? t("termDetail.shareText", { word: term.word }) : "",
        url: window.location.href,
      });
    } catch {
      navigator.clipboard.writeText(window.location.href);
      toast.success(t("termDetail.linkCopied"));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <section className="relative bg-gradient-to-br from-[hsl(220,40%,13%)] to-[hsl(218,35%,22%)] py-12">
          <div className="container">
            <div className="mb-4 h-12 w-64 animate-pulse rounded-lg bg-white/20" />
            <div className="h-6 w-48 animate-pulse rounded-lg bg-white/20" />
          </div>
        </section>
        <section className="py-12">
          <div className="container max-w-4xl">
            <div className="from-secondary/50 to-secondary/30 h-64 animate-pulse rounded-xl bg-gradient-to-br" />
          </div>
        </section>
      </div>
    );
  }

  if (!term) {
    return (
      <div className="from-secondary/30 to-background flex min-h-screen items-center justify-center bg-gradient-to-br">
        <div className="p-8 text-center">
          <div className="bg-muted mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full">
            <BookOpen className="text-muted-foreground h-12 w-12" />
          </div>
          <h1 className="text-foreground mb-3 font-serif text-3xl font-bold">
            {t("termDetail.notFound")}
          </h1>
          <p className="text-muted-foreground mb-8 max-w-md">
            {t("termDetail.notFoundBody")}
          </p>
          <Button
            onClick={() => navigate("/browse")}
            className="bg-primary hover:bg-primary/90 btn-shimmer"
          >
            {t("termDetail.browseAllTerms")}
          </Button>
        </div>
      </div>
    );
  }

  const pronunciationAudioSrc =
    term.audio_url != null ? String(term.audio_url).trim() : "";

  return (
    <div className="min-h-screen">
      {/* Hero Header */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[hsl(220,40%,13%)] via-[hsl(220,38%,18%)] to-[hsl(218,35%,22%)]">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="bg-primary/20 float absolute -right-20 -top-20 h-60 w-60 rounded-full blur-3xl" />
          <div className="bg-gold/15 float-delayed absolute -bottom-20 -left-20 h-80 w-80 rounded-full blur-3xl" />
        </div>

        {/* Decorative Pattern */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 50'%3E%3Ccircle cx='10' cy='10' r='1' fill='%23fff'/%3E%3Ccircle cx='30' cy='15' r='1' fill='%23fff'/%3E%3Ccircle cx='50' cy='8' r='1' fill='%23fff'/%3E%3Ccircle cx='70' cy='20' r='1' fill='%23fff'/%3E%3Ccircle cx='90' cy='12' r='1' fill='%23fff'/%3E%3C/svg%3E")`,
            backgroundSize: "150px 75px",
          }}
        />

        <div className="container relative py-10">
          <Button
            variant="ghost"
            onClick={() => navigate("/browse")}
            className="mb-6 text-white/70 hover:bg-white/10 hover:text-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("termDetail.backToBrowse")}
          </Button>

          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex min-w-0 flex-1 flex-col items-stretch gap-4 sm:flex-row sm:items-start md:gap-6 lg:gap-10">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/10 backdrop-blur-sm sm:mx-0 sm:h-20 sm:w-20">
                <span className="text-primary font-serif text-3xl font-bold md:text-4xl">
                  {wordFirstLetter(term.word)}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="mb-3 break-words font-serif text-3xl font-bold text-white md:text-5xl">
                  {term.word}
                </h1>
                {pronunciationAudioSrc.length > 0 ? (
                  <div className="mt-3 w-full max-w-none overflow-hidden rounded-xl border border-white/20 bg-white/[0.08] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] backdrop-blur-md sm:rounded-2xl">
                    <div className="from-primary via-gold/90 to-primary h-px bg-gradient-to-r opacity-95" />
                    <div className="px-4 py-4 sm:px-5 sm:py-5">
                      <ThemedAudioPlayer
                        src={pronunciationAudioSrc}
                        ariaLabel={t("termDetail.recordedPronunciation")}
                      />
                      {term.pronunciation?.trim() ? (
                        <p className="text-white/75 mt-3 flex items-center gap-2 border-t border-white/10 pt-3 text-sm">
                          <Volume2 className="text-white/55 h-4 w-4 shrink-0" />
                          <span className="italic">
                            /{term.pronunciation.trim()}/
                          </span>
                        </p>
                      ) : null}
                    </div>
                  </div>
                ) : term.pronunciation ? (
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      const utterance = new SpeechSynthesisUtterance(term.word);
                      utterance.rate = 0.8;
                      utterance.lang = "en-US";
                      window.speechSynthesis.cancel();
                      window.speechSynthesis.speak(utterance);
                    }}
                    className="mt-1 flex w-fit cursor-pointer items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-white/70 transition-colors hover:bg-white/20"
                  >
                    <Volume2 className="h-4 w-4" />
                    <span className="italic">
                      /{term.pronunciation}/
                    </span>
                  </button>
                ) : null}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleShare}
              className="shrink-0 self-start rounded-full text-white/70 hover:bg-white/10 hover:text-white md:self-auto"
            >
              <Share2 className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full"
          >
            <path
              d="M0 40V20C360 35 720 5 1080 20C1260 27.5 1350 32.5 1440 35V40H0Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Content */}
      <section className="relative overflow-hidden py-8 md:py-12">
        <div className="bg-primary/5 absolute right-0 top-0 h-96 w-96 -translate-y-1/2 translate-x-1/2 rounded-full blur-3xl" />

        <div className="container relative max-w-4xl">
          {/* All Meanings in One Card */}
          <Card className="border-border/50 mb-8 overflow-hidden shadow-lg">
            <div className="from-primary via-gold to-primary h-1.5 bg-gradient-to-r" />
            <CardContent className="p-0">
              {/* English */}
              {term.meaning_english && (
                <div className="border-border/40 border-b p-5 md:p-8">
                  <Badge className="mb-3 border-0 bg-blue-500/10 px-3 py-1 text-sm text-blue-600">
                    {t("term.english")}
                  </Badge>
                  <p className="text-foreground text-lg leading-relaxed break-words md:text-xl">
                    {term.meaning_english}
                  </p>
                </div>
              )}

              {/* Hindi */}
              {term.meaning_hindi && (
                <div className="border-border/40 border-b p-5 md:p-8">
                  <div className="mb-3 flex items-center gap-2">
                    <Badge className="bg-primary/10 text-primary border-0 px-3 py-1 text-sm">
                      {t("term.hindi")}
                    </Badge>
                    <span className="text-muted-foreground font-devanagari text-sm">
                      हिंदी
                    </span>
                  </div>
                  <p className="font-devanagari text-foreground text-lg leading-relaxed break-words md:text-xl">
                    {term.meaning_hindi}
                  </p>
                </div>
              )}

              {/* Gujarati */}
              {term.meaning_gujarati && (
                <div className="p-5 md:p-8">
                  <div className="mb-3 flex items-center gap-2">
                    <Badge className="bg-accent/10 text-accent border-0 px-3 py-1 text-sm">
                      {t("term.gujarati")}
                    </Badge>
                    <span className="text-muted-foreground font-devanagari text-sm">
                      ગુજરાતી
                    </span>
                  </div>
                  <p className="font-devanagari text-foreground text-lg leading-relaxed break-words md:text-xl">
                    {term.meaning_gujarati}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Example Usage */}
          {term.example_usage && (
            <Card className="border-border/50 from-secondary/50 to-secondary/20 mb-8 bg-gradient-to-br shadow-lg">
              <CardContent className="p-5 md:p-8">
                <h3 className="text-muted-foreground mb-4 text-sm font-semibold uppercase tracking-wider">
                  {t("termDetail.exampleUsage")}
                </h3>
                <p className="text-foreground text-lg italic leading-relaxed break-words">
                  "{term.example_usage}"
                </p>
              </CardContent>
            </Card>
          )}

          {/* Quick Info + Related Terms Row */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Quick Info */}
            <Card className="border-border/50 shadow-lg">
              <CardContent className="p-5 md:p-6">
                <h3 className="text-muted-foreground mb-4 text-sm font-semibold uppercase tracking-wider">
                  {t("termDetail.quickInfo")}
                </h3>
                <div className="space-y-3">
                  <div className="bg-secondary/30 flex items-center justify-between rounded-lg p-3">
                    <span className="text-muted-foreground">
                      {t("termDetail.firstLetter")}
                    </span>
                    <button
                      onClick={() =>
                        navigate(
                          `/browse?letter=${wordFirstLetter(term.word)}`
                        )
                      }
                      className="text-primary flex items-center gap-1 font-semibold hover:underline"
                    >
                      {wordFirstLetter(term.word)}
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="bg-secondary/30 flex items-center justify-between rounded-lg p-3">
                    <span className="text-muted-foreground">
                      {t("termDetail.languagesLabel")}
                    </span>
                    <span className="text-foreground text-sm font-medium">
                      {[
                        term.meaning_english && "EN",
                        term.meaning_hindi && "HI",
                        term.meaning_gujarati && "GU",
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Related Terms */}
            {filteredRelatedTerms && filteredRelatedTerms.length > 0 && (
              <Card className="border-border/50 shadow-lg">
                <CardContent className="p-5 md:p-6">
                  <h3 className="text-muted-foreground mb-4 text-sm font-semibold uppercase tracking-wider">
                    {t("termDetail.relatedTerms")}
                  </h3>
                  <div className="space-y-2">
                    {filteredRelatedTerms.map(relatedTerm => (
                      <button
                        key={relatedTerm.id}
                        onClick={() => navigate(`/term/${relatedTerm.id}`)}
                        className="bg-secondary/30 hover:bg-primary/5 hover:border-primary/20 group w-full rounded-lg p-3 text-left transition-all"
                      >
                        <span className="text-foreground group-hover:text-primary font-semibold transition-colors">
                          {relatedTerm.word}
                        </span>
                        {relatedTerm.pronunciation && (
                          <p className="text-muted-foreground text-sm">
                            /{relatedTerm.pronunciation}/
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermDetail;
